import React, { useEffect, useState } from "react";
import {
  Container,
  Title,
  Button,
  Card,
  Text,
  Group,
  Stack,
  Badge,
  Modal,
  TextInput,
  LoadingOverlay,
  Accordion,
  List,
  ThemeIcon,
  Alert,
  Divider,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { useWebsitesStore } from "./websitesStore";
import {
  IconPlus,
  IconWorld,
  IconCalendar,
  IconFile,
  IconBrandGoogle,
  IconDownload,
  IconCheck,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { fetchWebsiteById } from "./websitesApi";

export default function WebsiteList() {
  const {
    websites,
    isLoading,
    selectedWebsite,
    fetchWebsites,
    fetchWebsiteByBaseUrl,
    createWebsite,
    clearSelectedWebsite,
    gscConnected,
    gscSites,
    gscLoading,
    checkGscConnection,
    fetchGscSites,
    importGscSite,
  } = useWebsitesStore();

  const [opened, { open, close }] = useDisclosure(false);
  const [gscModalOpened, { open: openGscModal, close: closeGscModal }] =
    useDisclosure(false);
  const [newWebsiteUrl, setNewWebsiteUrl] = useState("");
  const [importedSites, setImportedSites] = useState(new Set());

  useEffect(() => {
    fetchWebsites();
    checkGscConnection();
  }, [fetchWebsites, checkGscConnection]);

  const handleCreateWebsite = async () => {
    if (!newWebsiteUrl.trim()) {
      notifications.show({
        title: "Error",
        message: "Please enter a valid URL",
        color: "red",
      });
      return;
    }

    const result = await createWebsite(newWebsiteUrl);
    if (result) {
      setNewWebsiteUrl("");
      close();
    }
  };

  // const handleViewWebsite = async (baseUrl) => {
  //   await fetchWebsiteByBaseUrl(baseUrl);
  // };

  const handleViewWebsite = async (id) => {
    await fetchWebsiteById(id);
  };

  const handleFetchGscSites = async () => {
    const sites = await fetchGscSites();
    if (sites && sites.length > 0) {
      openGscModal();
    }
  };

  const handleImportSite = async (siteUrl) => {
    const result = await importGscSite(siteUrl);
    if (result) {
      setImportedSites((prev) => new Set([...prev, siteUrl]));
    }
  };

  const isAlreadyAdded = (siteUrl) => {
    const cleanUrl = siteUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");
    return (
      websites?.some(
        (w) =>
          w.baseUrl.replace(/^https?:\/\//, "").replace(/\/$/, "") === cleanUrl
      ) || importedSites.has(siteUrl)
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Container size="lg" py="xl">
      <Group justify="space-between" mb="xl">
        <Title order={1}>Crawled Websites</Title>
        <Group>
          {gscConnected && (
            <Button
              leftSection={<IconBrandGoogle size={16} />}
              onClick={handleFetchGscSites}
              variant="light"
              loading={gscLoading}
            >
              Import from GSC
            </Button>
          )}
          <Button leftSection={<IconPlus size={16} />} onClick={open}>
            Add Website
          </Button>
        </Group>
      </Group>

      {gscConnected && (
        <Alert
          icon={<IconBrandGoogle />}
          title="Google Search Console Connected"
          color="green"
          mb="md"
        >
          You can import verified sites from your Google Search Console account.
        </Alert>
      )}

      <LoadingOverlay visible={isLoading} />

      {websites && websites.length > 0 ? (
        <Stack gap="md">
          {websites.map((website) => (
            <Card
              key={website.id}
              shadow="sm"
              padding="lg"
              radius="md"
              withBorder
            >
              <Group justify="space-between" mb="md">
                <Group>
                  <ThemeIcon color="blue" size="lg" radius="xl">
                    <IconWorld size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="lg" fw={600}>
                      {website.baseUrl}
                    </Text>
                    <Group gap="xs">
                      <IconCalendar size={14} />
                      <Text size="sm" c="dimmed">
                        Next crawl: {formatDate(website.nextCrawl)}
                      </Text>
                    </Group>
                  </div>
                </Group>
                <Button
                  variant="light"
                  size="sm"
                  onClick={() => handleViewWebsite(website.id)}
                >
                  View Details
                </Button>
              </Group>
            </Card>
          ))}
        </Stack>
      ) : (
        !isLoading && (
          <Card padding="xl" radius="md" withBorder>
            <Text ta="center" c="dimmed" size="lg">
              No websites found. Add a website to start crawling.
            </Text>
          </Card>
        )
      )}

      {selectedWebsite && (
        <Modal
          opened={!!selectedWebsite}
          onClose={clearSelectedWebsite}
          title={`Website Details: ${selectedWebsite.baseUrl}`}
          size="lg"
        >
          <Stack gap="md">
            <Group>
              <Badge color="blue" variant="light">
                ID: {selectedWebsite.id}
              </Badge>
              <Badge color="green" variant="light">
                Pages: {selectedWebsite.pages?.length || 0}
              </Badge>
            </Group>

            <div>
              <Text size="sm" fw={600} mb="xs">
                Next Crawl:
              </Text>
              <Text size="sm" c="dimmed">
                {formatDate(selectedWebsite.nextCrawl)}
              </Text>
            </div>

            {selectedWebsite.pages && selectedWebsite.pages.length > 0 && (
              <div>
                <Text size="sm" fw={600} mb="xs">
                  Crawled Pages:
                </Text>
                <Accordion>
                  {selectedWebsite.pages.map((page, index) => (
                    <Accordion.Item
                      key={page.id || index}
                      value={`page-${index}`}
                    >
                      <Accordion.Control>
                        <Group>
                          <ThemeIcon color="teal" size="sm" radius="xl">
                            <IconFile size={12} />
                          </ThemeIcon>
                          <Text size="sm" truncate style={{ maxWidth: 400 }}>
                            {page.url || page.title || `Page ${index + 1}`}
                          </Text>
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap="xs">
                          {page.url && (
                            <Text size="xs" c="dimmed">
                              <strong>URL:</strong> {page.url}
                            </Text>
                          )}
                          {page.title && (
                            <Text size="xs" c="dimmed">
                              <strong>Title:</strong> {page.title}
                            </Text>
                          )}
                          {page.content && (
                            <Text size="xs" c="dimmed">
                              <strong>Content Preview:</strong>{" "}
                              {page.content.substring(0, 200)}...
                            </Text>
                          )}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </div>
            )}
          </Stack>
        </Modal>
      )}

      <Modal opened={opened} onClose={close} title="Add New Website">
        <Stack gap="md">
          <TextInput
            label="Website URL"
            placeholder="https://example.com"
            value={newWebsiteUrl}
            onChange={(event) => setNewWebsiteUrl(event.currentTarget.value)}
            required
          />
          <Group justify="flex-end">
            <Button variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button onClick={handleCreateWebsite}>Add Website</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={gscModalOpened}
        onClose={closeGscModal}
        title="Import from Google Search Console"
        size="lg"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Select verified sites from your Google Search Console to add to your
            crawled websites.
          </Text>

          {gscSites && gscSites.length > 0 ? (
            <Stack gap="xs">
              {gscSites.map((site) => {
                const alreadyAdded = isAlreadyAdded(site.siteUrl);

                return (
                  <Card key={site.siteUrl} padding="sm" withBorder>
                    <Group justify="space-between">
                      <div style={{ flex: 1 }}>
                        <Group gap="xs">
                          <ThemeIcon color="blue" size="sm" radius="xl">
                            <IconWorld size={14} />
                          </ThemeIcon>
                          <div>
                            <Text size="sm" fw={500}>
                              {site.siteUrl}
                            </Text>
                            <Badge size="xs" variant="light" color="gray">
                              {site.permissionLevel}
                            </Badge>
                          </div>
                        </Group>
                      </div>

                      {alreadyAdded ? (
                        <Tooltip label="Already added">
                          <ActionIcon color="green" variant="light" size="lg">
                            <IconCheck size={18} />
                          </ActionIcon>
                        </Tooltip>
                      ) : (
                        <Button
                          size="xs"
                          leftSection={<IconDownload size={14} />}
                          onClick={() => handleImportSite(site.siteUrl)}
                        >
                          Import
                        </Button>
                      )}
                    </Group>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Text ta="center" c="dimmed" py="xl">
              No verified sites found in your Google Search Console.
            </Text>
          )}

          <Divider />

          <Group justify="flex-end">
            <Button variant="outline" onClick={closeGscModal}>
              Close
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
